import { useState, useEffect } from 'react';
import { CatalogItem } from '../types/catalog';
import { catalogClient } from '../services/grpc';
import * as pb from '../generated/catalog_pb';
import { Button } from "./ui/button";
import { ShoppingCart, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export interface AnalogsProps {
  product: CatalogItem;
  onAddToCart: (product: CatalogItem) => void;
  onViewDetails: (product: CatalogItem) => void;
}

export function Analogs({ product, onAddToCart, onViewDetails }: AnalogsProps) {
  const [analogs, setAnalogs] = useState<CatalogItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const kv = Object.fromEntries(
      (product.props?.prop ?? []).map(p => [p.code, p.value])
    );

    const analogString = kv['analog'];
    if (!analogString) return;

    const allAnalogs = analogString.split('/').map(a => a.trim()).filter(a => a.length > 2);

    // Filter out the current product itself that the API always stupidly returns first
    const codes = allAnalogs.filter(code => code !== product.code);

    if (codes.length === 0) return;

    setLoading(true);

    // ✅ GET ALL ANALOGS IN ONE SINGLE REQUEST
    const req = new pb.ItemsByAnalogReq();
    req.setLimit(codes.length);
    req.setCode(product.code);
    catalogClient.getAnalogList(req, {}, (err, response) => {
      setLoading(false);

      if (err) {
        console.error('Failed to load analogs', err);
        return;
      }

      const mapped = response.getItemsList().map(item => ({
        code: item.getCode(),
        name: item.getName(),
        desc: item.getDesc(),
        title: item.getTitle(),
        pic: item.getPic(),
        id: item.getId(),
        price: item.getPrice(),
        qty: item.getQty(),
        props: {
          prop: item.getProps()?.getPropList().map(p => ({
            code: p.getCode(),
            name: p.getName(),
            value: p.getValue(),
          })) || [],
          storage:item.getStoragelistList().map(p=>({
              storage: p.getStorage,
              price: p.getPrice,
              qty: p.getQty,
            })) || [],
        }
      }));

      // ✅ Preserve the original order from analog list
      const sorted = codes
        .map(code => mapped.find(a => a.code === code))
        .filter(Boolean) as CatalogItem[];

      setAnalogs(mapped);
    });

  }, [product]);


  if (loading) return <div className="text-sm text-gray-500 py-3">Loading analogs...</div>;
  if (analogs.length === 0) return null;


  return (
    <div className="mt-6 border-t pt-4">
      <h3 className="font-semibold text-base mb-3">Аналоги / заменители</h3>

      <div className="grid gap-2">
        {analogs.map(analog => (
          <div 
            key={analog.code}
            className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 hover:bg-blue-50 transition-colors"
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="font-medium text-sm">{analog.code}</p>
                <p className="text-sm">{analog.name}</p>
              </div>
             
            </div>

            <div className="flex gap-1 shrink-0">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onViewDetails(analog)}
                className="h-8 w-8 p-0"
              >
                <ExternalLink className="h-4 w-4" />
              </Button>


            </div>
          </div>
        ))}
      </div>
    </div>
  );
}